import { Switch } from '@src/components/Switch';
import StepProgress from '@src/features/user/checkout/layout/StepProgress';
import { useCheckoutStore } from '@src/stores/checkoutStore';
import { ProductAccountTypes, TradingPlatForms } from '@src/types';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@src/features/ui';
import { useNavigate } from 'react-router-dom';
import { getProductsAccountsOnboarding } from '@src/services/products';
import { useGetMe } from '@src/hooks/useGetMe';
import { ContactSupportPopup } from '@features/core/ContactSupportPopup.tsx';

import { accountTypeData, iconsImgPackage, iconsPackage } from './data';

export const SelectAccountPage = () => {
  const {
    platform,
    sizeIndex,
    typeIndex,
    packages,
    setPackages,
    setSizeIndex,
    isCompetitionAccount: _isCompetitionAccount,
    setIsCompetitionAccount: _setIsCompetitionAccount,
    isPurchaseMode,
    setPlatform,
  } = useCheckoutStore();
  const { me } = useGetMe();
  const [isCompetitionAccount, setIsCompetitionAccount] = useState(
    _isCompetitionAccount
  );
  const [selectedIdx, setSelectedIdx] = useState<number | undefined>(sizeIndex);
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  const navigate = useNavigate();

  const fetchProducts = async () => {
    const account_type = (() => {
      if (typeIndex === 2) return 'straight_to_pa';
      if (typeIndex === 1) return 'practice_account';
      if (typeIndex === 3) return 'specialty_account';

      return 'practice_account';
    })();

    try {
      const response = await getProductsAccountsOnboarding({
        datafeed: platform !== 'all' ? platform : undefined,
        account_types: [account_type as ProductAccountTypes],
      });
      const mappedData = response.data.map((pkg: any) => ({
        id: pkg.id,
        display_name_app: pkg.title,
        billing_plan: pkg.billing_plan,
        account_benefits: pkg.account_benefits,
      }));

      setPackages(mappedData);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    // If in purchase mode and platform is not set, get it from me.platform
    if (isPurchaseMode && !platform && me?.platform) {
      setPlatform(me.platform);

      return;
    }

    // If not in purchase mode and no platform, redirect to platform selection
    if (!isPurchaseMode && !platform) {
      navigate('/select-trading-platform');

      return;
    }

    // Fetch products when platform is available
    if (platform) {
      fetchProducts().then(() => null);
    }
  }, [navigate, platform, isPurchaseMode, me, setPlatform]);

  const onContinue = () => {
    setSizeIndex(selectedIdx);
    _setIsCompetitionAccount(!!isCompetitionAccount);
    navigate('/add-ons');
  };

  const goBack = () => {
    navigate('/select-trading-account-type');
    setSizeIndex(undefined);
    _setIsCompetitionAccount(false);
  };

  const selectedPackage = useMemo(() => {
    if (selectedIdx === undefined || !packages) return null;

    return packages[selectedIdx];
  }, [selectedIdx, packages]);

  const accountPrice = useMemo(() => {
    if (!selectedPackage) return 0;

    return selectedPackage.billing_plan?.first_price ?? 0;
  }, [selectedPackage]);

  const totalDueTodayPrice = useMemo(() => {
    if (
      platform === TradingPlatForms.RITHMIC_DATA_FEED &&
      isCompetitionAccount
    ) {
      return (selectedPackage?.billing_plan?.first_price ?? 0) + 35;
    }

    return (selectedPackage?.billing_plan?.first_price ?? 0) + 20;
  }, [selectedPackage, accountPrice, platform, isCompetitionAccount]);

  const priceStr = useMemo(() => {
    return '$15.00';
  }, [isCompetitionAccount]);

  if (!platform) return null;

  return (
    <>
      <div className="pt-4">
        <div className="max-w-[1440px] m-auto px-5 py-4 flex flex-row justify-between gap-x-10 bg-[#FFFFFF0D] backdrop-blur-[200px]">
          <div className="flex-1">
            <div className="mb-8">
              <StepProgress
                accountType={typeIndex}
                current={isPurchaseMode ? 2 : 3}
                platform={platform}
                selectedPackage={selectedPackage}
                skipPlatform={isPurchaseMode}
                total={isPurchaseMode ? 4 : 5}
                onClick={(step) => {
                  if (!isPurchaseMode && !step)
                    navigate('/select-trading-platform');
                }}
              />
            </div>
            <div className="flex flex-col-reverse lg:flex-row gap-[40px]">
              <div className="flex-1">
                <h2 className="font-bold text-[23px] leading-[100%] tracking-normal text-white mb-2">
                  Choose Base Account Size
                </h2>
                <p className="pt-[10px] font-medium text-[16px] leading-[100%] tracking-normal text-white mb-[36px]">
                  Select the base account size that best fits your trading needs
                </p>
                <div className="grid grid-cols-2 gap-[20px]">
                  {packages?.map((pkg, index) => (
                    <div
                      key={pkg.id}
                      className={`bg-[${selectedIdx !== index ? '#25334880' : '#007AFE'}] border border-[#33455F] h-[287px] gap-[20px] rounded-[10px] pt-[21px] pr-[18px] pb-[21px] pl-[18px] flex flex-col transition-all duration-150 relative ${selectedIdx === index ? 'border-[#5B7FFF] shadow-lg' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedIdx(index)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="text-white text-[24px] font-bold truncate block leading-[1] h-[30px]"
                          style={{
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            display: '-webkit-box',
                          }}
                        >
                          {pkg.display_name_app}
                        </span>

                        <div
                          className={`${selectedIdx === index ? 'bg-[#FFFFFF]' : 'bg-[#2A394C] border-[1px] border-[#33455F]'} h-5 w-5 rounded-[20px] flex items-center justify-center`}
                        >
                          {selectedIdx === index && (
                            <svg
                              className="h-4 w-4 text-[#007AFE]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M5 13l4 4L19 7"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      <ul className="text-[#B1B5C3] text-[15px] gap-[15px] flex flex-col">
                        <li className="flex items-center gap-[10px]">
                          <span className=" font-medium text-[16px] text-white whitespace-pre-line leading-[200%]">
                            {pkg.account_benefits}
                          </span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <div className="w-full h-[2px] bg-[#33455F] mb-3" />
                        <div className="flex items-center justify-between text-white">
                          <div className="text-white text-[16px] font-medium">
                            Starting at
                          </div>
                          <div className="text-white text-[20px] font-bold">
                            ${pkg.billing_plan?.first_price}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Custom Account Card */}
                  <div
                    className="cursor-pointer bg-[#232B36] rounded-[14px] border-2 flex flex-col items-center justify-center p-[30px] gap-[15px]"
                    style={{
                      borderStyle: 'dashed',
                      borderColor: '#33455F',
                      borderWidth: 1,
                    }}
                    onClick={() => setIsContactSupportOpen(true)}
                  >
                    <svg
                      fill="none"
                      height="45"
                      viewBox="0 0 60 60"
                      width="45"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M30 5C16.1925 5 5 16.1925 5 30C5 43.8075 16.1925 55 30 55C43.8075 55 55 43.8075 55 30C55 16.1925 43.8075 5 30 5ZM42.5 32.5H32.5V42.5H27.5V32.5H17.5V27.5H27.5V17.5H32.5V27.5H42.5V32.5Z"
                        fill="#FFFFFF"
                      />
                    </svg>
                    <div className="text-white text-[30px] font-bold">
                      Custom Account
                    </div>
                    <div className="text-[#B1B5C3] text-[14px] text-center">
                      Need a different account size?
                    </div>
                    <button className="w-[131px] h-[44px] rounded-[10px] gap-[10px] p-[10px] text-white bg-[#007AFE] font-bold text-[14px]">
                      Contact Support
                    </button>
                    <div className="text-[#B1B5C3] text-[16px] leading-[100%] text-center mt-auto">
                      *Min quantity required
                    </div>
                  </div>
                </div>
              </div>
              {/* Sidebar */}
              <div className="relative lg:w-[340px] base:w-full bg-[#1F2734] rounded-[20px] pt-[30px] pb-[30px] pl-[24px] pr-[24px] flex flex-col gap-[24px]">
                <div className="sticky top-[30px] bg-[#253348] pt-[30px] pb-[30px] pl-[24px] pr-[24px] border border-[#33455F] rounded-[10px] gap-[30px] flex flex-col">
                  <span className="font-bold text-[16px] leading-[100%] tracking-normal text-white">
                    Your Selection
                  </span>
                  <div>
                    <div className="flex items-center gap-[23px] h-[46px]">
                      <svg
                        fill="none"
                        height="30"
                        viewBox="0 0 40 40"
                        width="30"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                          fill="#14CA74"
                        />
                      </svg>
                      <div>
                        <div className="text-[12px] text-[#CECECE]">
                          Trading Platform
                        </div>
                        <div className="flex items-center gap-[10px]  mt-[5px]">
                          {
                            iconsImgPackage[
                              platform as keyof typeof iconsImgPackage
                            ].icon
                          }
                          <span className="font-semibold text-[14px] text-white">
                            {
                              iconsImgPackage[
                                platform as keyof typeof iconsImgPackage
                              ].text
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="w-[30px] flex justify-center ">
                      <div className="bg-[#33455F] w-[4px] h-[26px] rounded" />
                    </div>
                    <div className="flex items-center gap-[23px] h-[46px]">
                      <svg
                        fill="none"
                        height="30"
                        viewBox="0 0 40 40"
                        width="30"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                          fill="#14CA74"
                        />
                      </svg>
                      <div>
                        <div className="text-[12px] text-[#CECECE]">
                          Account Type
                        </div>
                        <div className="font-semibold text-[14px]">
                          {accountTypeData[(typeIndex || 1) - 1]?.title}
                        </div>
                      </div>
                    </div>
                    <div className="w-[30px] flex justify-center ">
                      <div className="bg-[#33455F] w-[4px] h-[26px] rounded" />
                    </div>
                    <div className="flex items-center gap-[23px] h-[46px]">
                      <svg
                        fill="none"
                        height="30"
                        viewBox="0 0 40 40"
                        width="30"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M17.6033 26.5133L28.2633 15.8533L27.0833 14.6733L17.6033 24.1533L12.8533 19.4033L11.6733 20.5833L17.6033 26.5133ZM20.005 35C17.9317 35 15.9817 34.6067 14.155 33.82C12.3294 33.0322 10.7411 31.9633 9.39 30.6133C8.03889 29.2633 6.96944 27.6767 6.18167 25.8533C5.39389 24.03 5 22.0806 5 20.005C5 17.9294 5.39389 15.9794 6.18167 14.155C6.96833 12.3294 8.03556 10.7411 9.38333 9.39C10.7311 8.03889 12.3183 6.96944 14.145 6.18167C15.9717 5.39389 17.9217 5 19.995 5C22.0683 5 24.0183 5.39389 25.845 6.18167C27.6706 6.96833 29.2589 8.03611 30.61 9.385C31.9611 10.7339 33.0306 12.3211 33.8183 14.1467C34.6061 15.9722 35 17.9217 35 19.995C35 22.0683 34.6067 24.0183 33.82 25.845C33.0333 27.6717 31.9644 29.26 30.6133 30.61C29.2622 31.96 27.6756 33.0294 25.8533 33.8183C24.0311 34.6072 22.0817 35.0011 20.005 35Z"
                          fill="#33455F"
                        />
                      </svg>
                      <div>
                        <div className="text-[12px] text-[#CECECE]">
                          Account Size
                        </div>
                        <div className="font-semibold text-[14px] break-words whitespace-pre-line max-w-[180px] truncate">
                          {selectedPackage ? (
                            (selectedPackage.display_name_app ?? '') +
                            ' Account'
                          ) : (
                            <span className="text-[14px] font-normal">
                              Select Base Account
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-[20px]">
                      <div className="mt-[30px] h-[3px] bg-[#33455F] w-full" />
                      <div className="flex flex-col gap-[10px]">
                        <div className="flex items-center gap-[23px] justify-between leading-[17px]">
                          <div className="text-[14px] font-medium text-white">
                            {
                              iconsImgPackage[
                                platform as keyof typeof iconsImgPackage
                              ].text
                            }
                          </div>
                          <div className="font-medium text-[14px]">$20.00</div>
                        </div>
                        <div className="flex items-center gap-[23px] justify-between leading-[17px]">
                          <div className="text-[14px] font-medium text-white">
                            {!selectedPackage
                              ? 'Choose account'
                              : `${selectedPackage.display_name_app} Account (Monthly)`}
                          </div>
                          <div className="font-medium text-[14px]">
                            ${accountPrice.toFixed(2)}
                          </div>
                        </div>
                        {platform === TradingPlatForms.RITHMIC_DATA_FEED &&
                          isCompetitionAccount && (
                            <div className="flex items-center gap-[23px] justify-between leading-[17px]">
                              <div className="text-[14px] font-medium text-white">
                                Competition Account
                              </div>
                              <div className="font-medium text-[14px]">
                                $15.00
                              </div>
                            </div>
                          )}
                      </div>
                      <div className="h-[3px] bg-[#33455F] w-full" />

                      <div className="flex items-center gap-[23px] justify-between h-[17px]">
                        <div className="text-[14px] font-bold text-white">
                          Total Due Today
                        </div>
                        <div className="font-bold text-[14px]">
                          ${totalDueTodayPrice.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {platform === TradingPlatForms.RITHMIC_DATA_FEED ? (
                  <div>
                    <span className="text-[18px] font-bold">
                      Only in Rithmic
                    </span>
                    <div className="mt-[10px]">
                      <div className="border border-[#C83837] rounded-[10px] bg-[#253348] pt-[20px] pb-[20px] pl-[18px] pr-[18px] flex flex-col gap-[20px]">
                        <div className="flex items-center justify-between">
                          <span>Competition Account</span>
                          <Switch
                            checked={!!isCompetitionAccount}
                            onChange={() =>
                              setIsCompetitionAccount(!isCompetitionAccount)
                            }
                          />
                        </div>
                        <div className="flex flex-col gap-[12px]">
                          <div className="flex items-center gap-[10px] font-medium">
                            {iconsPackage[0]} Unlimited trades per day
                          </div>
                          <div className="flex items-center gap-[10px] font-medium">
                            {iconsPackage[2]} Advanced risk algorithms
                          </div>
                          <div className="flex items-center gap-[10px] font-medium">
                            {iconsPackage[3]} 24/7 dedicated support
                          </div>
                        </div>
                        <div className="h-[3px] bg-[#33455F] w-full" />
                        <div className="flex items-center gap-[10px] font-medium justify-between">
                          <span>One-Time</span>
                          <span className="font-bold text-[20px]">
                            {priceStr}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full sticky bottom-0 backdrop-blur-[30px] bg-[linear-gradient(90deg,rgba(23,29,35,0.8)_0%,rgba(23,29,35,0.8)_100%)] border-t border-[#255589] h-[100px]">
        <div className="max-w-[1440px] m-auto px-6 lg:px-[60px] flex justify-between items-center h-full">
          <Button className="px-8" size="lg" variant="outline" onClick={goBack}>
            Previous: Select Account Type
          </Button>
          <Button
            className="px-8"
            disabled={selectedIdx === undefined}
            size="lg"
            variant="gradient"
            onClick={onContinue}
          >
            Next: Select Add-Ons
          </Button>
        </div>
      </div>
      <ContactSupportPopup
        open={isContactSupportOpen}
        onOpenChange={setIsContactSupportOpen}
      />
    </>
  );
};
